import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { LoanService } from '../../../../../services/loan/loan.service';
import { Loan, LoanRepaymentDto } from '../../../../../interfaces/responses/loan.interface';
import { ToastService } from '../../../../../services/general/toast.service';
import { PermissionService } from '../../../../../services/permission/permission.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { ProgressLoaderComponent } from '../../../../../components/loaders/progress-loader/progress-loader.component';
import { CustomInputComponent } from '../../../../../components/input/custom-input/custom-input.component';
import { CustomSelectInputComponent } from '../../../../../components/input/custom-select-input/custom-select-input.component';
import { ProductDetailsStore } from '../../../../../pages/products/product-details/product-details.store';

@Component({
  selector: 'app-applicant-details',
  imports: [CommonModule, ReactiveFormsModule, ProgressLoaderComponent, CustomInputComponent, CustomSelectInputComponent],
  templateUrl: './applicant-details.component.html',
  styleUrl: './applicant-details.component.scss'
})
export class ApplicantDetailsComponent implements OnInit {
  today = new Date().toISOString().split('T')[0];
  private route = inject(ActivatedRoute);
  private _router = inject(Router);
  private _fb = inject(FormBuilder);
  private loanService = inject(LoanService);
  private _toast = inject(ToastService);
  permissionService = inject(PermissionService);
  private destroyRef = inject(DestroyRef);
  private productDetailsStore = inject(ProductDetailsStore);

  loan: Loan | null = null;
  isLoading = true;

  loanRepayments: LoanRepaymentDto[] = [];
  isLoadingRepayments = false;

  get totalRepaid(): number {
    return this.loanRepayments.reduce((sum, r) => sum + +r.amount, 0);
  }

  get balance(): number {
    return +(this.loan?.totalRepayment ?? 0) - this.totalRepaid;
  }

  get repaymentProgress(): number {
    const total = +(this.loan?.totalRepayment ?? 0);
    if (!total) return 0;
    return Math.min((this.totalRepaid / total) * 100, 100);
  }

  // Record repayment modal
  showRepayModal = false;
  isSubmitting = false;
  repaymentTypes = [
    { label: 'Bank', value: 'BANK' },
    { label: 'Cash', value: 'CASH' },
    { label: 'Mobile Money', value: 'MOMO' },
  ];

  repayForm: FormGroup = this._fb.group({
    type: ['', Validators.required],
    transactionId: ['', Validators.required],
    amount: ['', [Validators.required, Validators.min(0.01)]],
    date: ['', Validators.required],
    comment: [''],
  });

  ngOnInit(): void {
    this.route.paramMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(params => {
        const applicationId = params.get('applicationId');
        if (!applicationId) {
          this._toast.error('Missing application ID in route');
          this.isLoading = false;
          return;
        }

        const storedLoan = this.productDetailsStore.selectedLoan();
        if (storedLoan && storedLoan.id === applicationId) {
          this.loan = storedLoan;
          this.isLoading = false;
          this.fetchLoanRepayments(applicationId);
        } else {
          this.fetchLoanRepayments(applicationId);
        }
      });
  }

  fetchLoanRepayments(loanId: string) {
    this.isLoadingRepayments = true;
    this.loanService.getRepayments({ loanId, paginate: false }).subscribe({
      next: (res: any) => {
        this.loanRepayments = Array.isArray(res) ? res : (res.data ?? []);
        if (!this.loan && this.loanRepayments.length > 0) {
          this.loan = this.loanRepayments[0].loan;
        }
        this.isLoading = false;
        this.isLoadingRepayments = false;
      },
      error: () => {
        this.isLoading = false;
        this.isLoadingRepayments = false;
      }
    });
  }

  onClose() {
    const productId = this.productDetailsStore.productId();
    if (productId) {
      this._router.navigate(['/product-details', productId, 'overview']);
    } else {
      this._router.navigate(['../..'], { relativeTo: this.route });
    }
  }

  openRepayModal(): void {
    this.repayForm.reset();
    this.repayForm.markAsPristine();
    this.repayForm.markAsUntouched();
    this.showRepayModal = true;
  }

  closeRepayModal(): void {
    this.showRepayModal = false;
  }

  submitRepayment(): void {
    if (this.repayForm.invalid) {
      this.repayForm.markAllAsTouched();
      return;
    }
    const loanReference = this.loan?.reference;
    const loanId = this.loan?.id;
    if (!loanReference || !loanId) return;

    this.isSubmitting = true;
    const { type, transactionId, amount, date, comment } = this.repayForm.value;
    this.loanService.createRepayment({ loanId: loanReference, type, transactionId, amount: +amount, date, comment }).subscribe({
      next: () => {
        this._toast.success('Repayment recorded successfully');
        this.closeRepayModal();
        this.isSubmitting = false;
        this.fetchLoanRepayments(loanId);
      },
      error: (err) => {
        this._toast.error(err?.message || 'Failed to record repayment');
        this.isSubmitting = false;
      }
    });
  }

}
